package com.appsyde.shared.modules.ticket.data.source

import com.appsyde.shared.modules.ticket.data.dto.AcquireLockResponseDto
import com.appsyde.shared.modules.ticket.data.dto.AssignTicketResponseDto
import com.appsyde.shared.modules.ticket.data.dto.LockedByOtherErrorDto
import com.appsyde.shared.modules.ticket.data.dto.RenewLockResponseDto
import com.appsyde.shared.modules.ticket.data.dto.ReleaseLockResponseDto
import com.appsyde.shared.modules.ticket.data.dto.SendTicketReplyRequestDto
import com.appsyde.shared.modules.ticket.data.dto.SendTicketReplyResponseDto
import com.appsyde.shared.modules.ticket.data.dto.AssignUpToResponseDto
import com.appsyde.shared.modules.ticket.data.dto.AssignTicketErrorDto
import com.appsyde.shared.modules.ticket.data.dto.OutboundAttachmentDto
import com.appsyde.shared.modules.ticket.data.dto.AddMemoryRequestDto
import com.appsyde.shared.modules.ticket.data.dto.ConfirmKeyRequestDto
import com.appsyde.shared.modules.ticket.data.dto.UpdateTicketStatusRequestDto
import com.appsyde.shared.modules.ticket.data.dto.TicketHistoryResponseDto
import com.appsyde.shared.modules.ticket.data.dto.TicketOriginalHistoryResponseDto
import com.appsyde.shared.modules.ticket.data.dto.TicketRespondResponseDto
import com.appsyde.shared.modules.ticket.data.dto.TicketsResponseDto
import com.appsyde.shared.modules.ticket.data.dto.UnassignTicketResponseDto
import com.appsyde.shared.modules.ticket.domain.AssignConflictException
import com.appsyde.shared.modules.ticket.domain.LockedByOtherException
import com.appsyde.shared.modules.ticket.domain.LockNotOwnedException
import com.appsyde.shared.modules.ticket.domain.NotAssigneeException
import com.appsyde.shared.network.requireSuccessOrBody
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.patch
import io.ktor.client.request.parameter
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.contentType
import kotlinx.serialization.json.Json

interface TicketsRemoteDataSource {
    suspend fun listTickets(limit: Int = 50, assignedToMe: Boolean = false): TicketsResponseDto

    suspend fun getRespond(ticketId: String): TicketRespondResponseDto

    /** Refreshes the ticket's thread from Gmail (background). Returns when done or on error. */
    suspend fun refreshThread(ticketId: String)

    suspend fun updateStatus(ticketId: String, status: String)

    suspend fun addMemory(ticketId: String, key: String, value: String)

    suspend fun confirmKey(ticketId: String, key: String)

    suspend fun getHistory(ticketId: String, limit: Int = 20): TicketHistoryResponseDto
    suspend fun getOriginalHistory(ticketId: String, limit: Int = 20): TicketOriginalHistoryResponseDto

    suspend fun sendReply(
        ticketId: String,
        body: String,
        subject: String? = null,
        to: String? = null,
        attachments: List<OutboundAttachmentDto> = emptyList()
    ): SendTicketReplyResponseDto

    suspend fun assignTicket(ticketId: String): AssignTicketResponseDto
    suspend fun assignUpTo(count: Int): AssignUpToResponseDto
    suspend fun takeoverTicket(ticketId: String): AssignTicketResponseDto
    suspend fun unassignTicket(ticketId: String): UnassignTicketResponseDto
    suspend fun acquireLock(ticketId: String): AcquireLockResponseDto
    suspend fun renewLock(ticketId: String): RenewLockResponseDto
    suspend fun releaseLock(ticketId: String): ReleaseLockResponseDto
}

class TicketsRemoteDataSourceImpl(
    private val http: HttpClient,
    private val baseUrl: String,
) : TicketsRemoteDataSource {

    override suspend fun listTickets(limit: Int, assignedToMe: Boolean): TicketsResponseDto {
        val safeLimit = limit.coerceIn(1, 100)
        return http.get("$baseUrl/tickets") {
            parameter("limit", safeLimit)
            if (assignedToMe) parameter("assignedToMe", "true")
        }.body()
    }

    override suspend fun getRespond(ticketId: String): TicketRespondResponseDto {
        val id = ticketId.trim()
        require(id.isNotBlank()) { "ticketId is required" }

        return http.get("$baseUrl/tickets/$id/respond").body()
    }

    override suspend fun refreshThread(ticketId: String) {
        val id = ticketId.trim()
        require(id.isNotBlank()) { "ticketId is required" }
        val res = http.post("$baseUrl/tickets/$id/refresh-thread") { }
        if (res.status.value !in 200..299) {
            val body = res.bodyAsText()
            throw RuntimeException("Refresh thread failed: HTTP ${res.status.value} – ${body.take(200)}")
        }
    }

    override suspend fun updateStatus(ticketId: String, status: String) {
        val id = ticketId.trim()
        require(id.isNotBlank()) { "ticketId is required" }
        http.patch("$baseUrl/tickets/$id/status") {
            contentType(ContentType.Application.Json)
            setBody(UpdateTicketStatusRequestDto(status = status))
        }
    }

    override suspend fun addMemory(ticketId: String, key: String, value: String) {
        val id = ticketId.trim()
        require(id.isNotBlank()) { "ticketId is required" }
        http.patch("$baseUrl/tickets/$id/memory") {
            contentType(ContentType.Application.Json)
            setBody(AddMemoryRequestDto(key = key.trim(), value = value.trim()))
        }
    }

    override suspend fun confirmKey(ticketId: String, key: String) {
        val id = ticketId.trim()
        require(id.isNotBlank()) { "ticketId is required" }
        http.patch("$baseUrl/tickets/$id/confirm") {
            contentType(ContentType.Application.Json)
            setBody(ConfirmKeyRequestDto(key = key.trim()))
        }
    }

    override suspend fun sendReply(
        ticketId: String,
        body: String,
        subject: String?,
        to: String?,
        attachments: List<OutboundAttachmentDto>
    ): SendTicketReplyResponseDto {
        val id = ticketId.trim()
        require(id.isNotBlank()) { "ticketId is required" }

        val payload = SendTicketReplyRequestDto(
            body = body,
            subject = subject,
            to = to,
            attachments = attachments
        )

        val res = http.post("$baseUrl/tickets/$id/send") {
            contentType(ContentType.Application.Json)
            setBody(payload)
        }
        val raw = res.requireSuccessOrBody("send_reply_failed")
        return json.decodeFromString(SendTicketReplyResponseDto.serializer(), raw)
    }

    override suspend fun getOriginalHistory(ticketId: String, limit: Int): TicketOriginalHistoryResponseDto {
        val id = ticketId.trim()
        require(id.isNotBlank()) { "ticketId is required" }

        val safeLimit = limit.coerceIn(1, 50)

        return http.get("$baseUrl/tickets/$id/history/originals") {
            parameter("limit", safeLimit)
        }.body()
    }

    override suspend fun getHistory(ticketId: String, limit: Int): TicketHistoryResponseDto {
        val id = ticketId.trim()
        require(id.isNotBlank()) { "ticketId is required" }

        val safeLimit = limit.coerceIn(1, 200)

        return http.get("$baseUrl/tickets/$id/history") {
            parameter("limit", safeLimit)
        }.body()
    }

    override suspend fun assignTicket(ticketId: String): AssignTicketResponseDto {
        val id = ticketId.trim()
        require(id.isNotBlank()) { "ticketId is required" }
        val res = http.post("$baseUrl/tickets/$id/assign") { }
        return parseAssignOrThrow(res, id)
    }

    override suspend fun assignUpTo(count: Int): AssignUpToResponseDto {
        val safeCount = count.coerceIn(1, 5)
        val res = http.post("$baseUrl/tickets/assign-up-to") {
            parameter("count", safeCount)
        }
        val raw = res.requireSuccessOrBody("assign_up_to_failed")
        return json.decodeFromString(AssignUpToResponseDto.serializer(), raw)
    }

    override suspend fun takeoverTicket(ticketId: String): AssignTicketResponseDto {
        val id = ticketId.trim()
        require(id.isNotBlank()) { "ticketId is required" }
        val res = http.post("$baseUrl/tickets/$id/takeover") { }
        return parseAssignOrThrow(res, id)
    }

    override suspend fun unassignTicket(ticketId: String): UnassignTicketResponseDto {
        val id = ticketId.trim()
        require(id.isNotBlank()) { "ticketId is required" }
        return http.delete("$baseUrl/tickets/$id/assign") { }.body()
    }

    override suspend fun acquireLock(ticketId: String): AcquireLockResponseDto {
        val id = ticketId.trim()
        require(id.isNotBlank()) { "ticketId is required" }
        val res = http.post("$baseUrl/tickets/$id/lock") { }
        return parseAcquireLockOrThrow(res, id)
    }

    override suspend fun renewLock(ticketId: String): RenewLockResponseDto {
        val id = ticketId.trim()
        require(id.isNotBlank()) { "ticketId is required" }
        val res = http.post("$baseUrl/tickets/$id/lock/renew") { }
        return parseRenewLockOrThrow(res, id)
    }

    override suspend fun releaseLock(ticketId: String): ReleaseLockResponseDto {
        val id = ticketId.trim()
        require(id.isNotBlank()) { "ticketId is required" }
        val res = http.delete("$baseUrl/tickets/$id/lock") { }
        return parseReleaseLockOrThrow(res, id)
    }

    private val json = Json { ignoreUnknownKeys = true }

    private suspend fun parseAssignOrThrow(res: HttpResponse, ticketId: String): AssignTicketResponseDto {
        val bodyText = res.bodyAsText()
        if (res.status.value in 200..299) {
            return json.decodeFromString(AssignTicketResponseDto.serializer(), bodyText)
        }
        if (res.status.value == 403) {
            throw RuntimeException("Takeover interdit: ${bodyText.takeIf { it.isNotBlank() } ?: "droits insuffisants" }")
        }
        if (res.status.value == 409) {
            val err = runCatching {
                json.decodeFromString(AssignTicketErrorDto.serializer(), bodyText)
            }.getOrNull()
            if (err != null) {
                throw AssignConflictException(
                    assignedToAccountUserId = err.assignedToAccountUserId,
                    assignedToDisplayName = err.assignedToDisplayName
                )
            }
        }
        throw RuntimeException("Assign ticket failed: HTTP ${res.status.value} – $bodyText")
    }

    private suspend fun parseAcquireLockOrThrow(res: HttpResponse, ticketId: String): AcquireLockResponseDto {
        val bodyText = res.bodyAsText()
        if (res.status.value in 200..299) {
            return json.decodeFromString(AcquireLockResponseDto.serializer(), bodyText)
        }
        if (res.status.value == 403) {
            throw NotAssigneeException("Only assignee can acquire lock")
        }
        if (res.status.value == 409) {
            val err = runCatching {
                json.decodeFromString(LockedByOtherErrorDto.serializer(), bodyText)
            }.getOrNull()
            if (err != null) {
                throw LockedByOtherException(
                    lockedByAccountUserId = err.lockedByAccountUserId,
                    lockedByDisplayName = err.lockedByDisplayName,
                    expiresAt = err.expiresAt
                )
            }
            throw LockNotOwnedException("Lock not owned or expired")
        }
        throw RuntimeException("Acquire lock failed: HTTP ${res.status.value} – $bodyText")
    }

    private suspend fun parseRenewLockOrThrow(res: HttpResponse, ticketId: String): RenewLockResponseDto {
        val bodyText = res.bodyAsText()
        if (res.status.value in 200..299) {
            return json.decodeFromString(RenewLockResponseDto.serializer(), bodyText)
        }
        if (res.status.value == 409) {
            throw LockNotOwnedException("Lock not owned or expired")
        }
        throw RuntimeException("Renew lock failed: HTTP ${res.status.value} – $bodyText")
    }

    private suspend fun parseReleaseLockOrThrow(res: HttpResponse, ticketId: String): ReleaseLockResponseDto {
        val bodyText = res.bodyAsText()
        if (res.status.value in 200..299) {
            return json.decodeFromString(ReleaseLockResponseDto.serializer(), bodyText)
        }
        if (res.status.value == 403) {
            throw LockNotOwnedException("Lock is held by another user")
        }
        throw RuntimeException("Release lock failed: HTTP ${res.status.value} – $bodyText")
    }
}

